import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import type { MenuProps } from 'antd'
import { Dropdown, message, Modal, Space } from 'antd'
import {
  BackwardFilled,
  DownloadOutlined,
  MailOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  FastBackwardOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  HomeOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import {
  SEND_MAIL_LEGAL_FORMALITY_MUTATION,
  RESET_LEGAL_FORMALITY_MUTATION,
  GENERATE_DOC_LEGAL_FORMALITY_MUTATION,
  STEP_BACK_LEGAL_FORMALITY_MUTATION,
  REGENERATE_STAFFS_LEGAL_FORMALITY_MUTATION,
  FORM_RECAP_URL_LEGAL_FORMALITY_MUTATION,
} from 'graphql/LegalFormalities'
import { CurrentLegalFormalityContext } from 'contexts/LegalFormalityContext'
import { CompanyStaffsContext } from 'contexts/CompanyStaffsContext'
import { openErrorPopup } from 'components/ConfirmationPopups'
import { createGlobalStyle } from 'styled-components'

const FormalityActionsMenu = () => {
  const { t } = useTranslation('legalFormalities')
  const { legalFormality, legalFormalityRefetch } = useContext(
    CurrentLegalFormalityContext
  )
  const { representantStaffs, refetch: staffsRefetch } =
    useContext(CompanyStaffsContext)
  const [legalFormalityGenerateDoc] = useMutation(
    GENERATE_DOC_LEGAL_FORMALITY_MUTATION
  )
  const [legalFormalityReset] = useMutation(RESET_LEGAL_FORMALITY_MUTATION)
  const [legalFormalitySendMail] = useMutation(
    SEND_MAIL_LEGAL_FORMALITY_MUTATION
  )
  const [legalFormalityStepBack] = useMutation(
    STEP_BACK_LEGAL_FORMALITY_MUTATION
  )
  const [legalFormalityRegenerateStaffs] = useMutation(
    REGENERATE_STAFFS_LEGAL_FORMALITY_MUTATION
  )
  const [legalFormalityFormRecapUrl] = useMutation(
    FORM_RECAP_URL_LEGAL_FORMALITY_MUTATION
  )

  const generateDoc = (docType: string, staffId: string) => {
    const variables = { id: legalFormality.id, docType, staffId }

    legalFormalityGenerateDoc({ variables }).then(response => {
      if (response.data.legalFormalityGenerateDoc?.error) {
        openErrorPopup(
          'Une erreur est survenue',
          response.data.legalFormalityGenerateDoc?.error
        )
      } else if (docType === 'form_recap') {
        // generated in background
        message.success(t('menuActions.generateDoc.form_recap_success'))
      } else {
        message.success(t('menuActions.generateDoc.success'))
        legalFormalityRefetch()
      }
    })
  }

  const sendMail = (mailType: string) => {
    const variables = { id: legalFormality.id, status: mailType }

    legalFormalitySendMail({ variables }).then(response => {
      if (response.data.legalFormalitySendMail?.error) {
        openErrorPopup(
          'Une erreur est survenue',
          response.data.legalFormalitySendMail?.error
        )
      } else {
        message.success(t('menuActions.sendMail.success'))
      }
    })
  }

  const downloadFormRecap = () => {
    const variables = { id: legalFormality.id }

    legalFormalityFormRecapUrl({ variables }).then(response => {
      const result = response.data.legalFormalityFormRecapUrl

      if (result?.error) {
        openErrorPopup('Une erreur est survenue', result.error)
      } else {
        window.open(result.url, '_blank', 'noopener')
      }
    })
  }

  const resetFormality = (resetType: string) => {
    const variables = { id: legalFormality.id, resetType: resetType }

    Modal.confirm({
      title: t(`menuActions.resetFormality.confirm.${resetType}.title`),
      icon:
        resetType === 'hard' ? (
          <CloseCircleOutlined style={{ color: 'red' }} />
        ) : (
          <ExclamationCircleOutlined />
        ),
      content: t(`menuActions.resetFormality.confirm.${resetType}.content`),
      okText: t(`menuActions.resetFormality.confirm.${resetType}.yes`),
      okType: resetType === 'hard' ? 'danger' : 'primary',
      cancelText: t(`menuActions.resetFormality.confirm.${resetType}.no`),
      onOk() {
        return legalFormalityReset({ variables }).then(response => {
          if (response.data.legalFormalityReset?.error) {
            openErrorPopup(
              'Une erreur est survenue',
              response.data.legalFormalityReset?.error
            )
          } else {
            message.success(t('menuActions.resetFormality.success'))
            window.location.reload()
          }
        })
      },
    })
  }

  const stepBack = (step: string) => {
    const variables = { id: legalFormality.id, status: step }
    const stepName = t(`show.progress.steps.name.${step}`)
    Modal.confirm({
      title: t(`menuActions.stepBack.confirm.title`),
      icon: <CloseCircleOutlined style={{ color: 'red' }} />,
      content: t('menuActions.stepBack.confirm.content', { stepName }),
      okText: t('menuActions.stepBack.confirm.yes', { stepName }),
      okType: 'danger',
      cancelText: t('menuActions.stepBack.confirm.no'),
      onOk() {
        return legalFormalityStepBack({ variables }).then(response => {
          if (response.data.legalFormalityStepBack?.error) {
            openErrorPopup(
              'Une erreur est survenue',
              response.data.legalFormalityStepBack?.error
            )
          } else {
            message.success(t('menuActions.stepBack.success'))
            window.location.reload()
          }
        })
      },
    })
  }

  const regenerateStaffs = () => {
    const variables = { id: legalFormality.id }

    Modal.confirm({
      title: t('menuActions.regenerateStaffs.confirm.title'),
      icon: <CloseCircleOutlined style={{ color: 'red' }} />,
      content: t('menuActions.regenerateStaffs.confirm.content'),
      okText: t('menuActions.regenerateStaffs.confirm.yes'),
      okType: 'danger',
      cancelText: t('menuActions.regenerateStaffs.confirm.no'),
      onOk() {
        return legalFormalityRegenerateStaffs({ variables }).then(response => {
          if (response.data.legalFormalityRegenerateStaffs?.error) {
            openErrorPopup(
              'Une erreur est survenue',
              response.data.legalFormalityRegenerateStaffs?.error
            )
          } else {
            message.success(t('menuActions.regenerateStaffs.success'))
            staffsRefetch()
          }
        })
      },
    })
  }

  const onClick: MenuProps['onClick'] = ({ key }) => {
    const [action, type, id] = key.split('-')
    switch (action) {
      case 'generateDoc':
        generateDoc(type, id)
        break
      case 'sendMail':
        sendMail(type)
        break
      case 'stepBack':
        stepBack(type)
        break
      case 'resetFormality':
        resetFormality(type)
        break
      case 'regenerateStaffs':
        regenerateStaffs()
        break
      case 'downloadFormRecap':
        downloadFormRecap()
    }
  }

  const listStepBack = () => {
    const rollbackableSteps = legalFormality.finishedSteps.filter(
      step => step !== 'paid'
    )

    return rollbackableSteps.map(step => ({
      key: 'stepBack-' + step,
      label: t(`show.progress.steps.name.${step}`),
    }))
  }

  const buildMenuItems = (): MenuProps['items'] => [
    {
      label: t('menuActions.generateDoc.title'),
      key: 'generateDoc',
      icon: <FileTextOutlined />,
      children: [
        {
          label: t('menuActions.generateDoc.subtitle.home_provision'),
          key: 'generateDoc.homeProvision',
          icon: <HomeOutlined />,
          children: representantStaffs.map(staff => ({
            key: 'generateDoc-home_provision-' + staff.id,
            label: `#${staff.id} ${staff.firstname} ${staff.lastname}`,
          })),
        },
        {
          label: t('menuActions.generateDoc.subtitle.form_recap'),
          key: 'generateDoc-form_recap',
          icon: <FileDoneOutlined />,
        },
      ],
    },
    {
      label: t('menuActions.sendMail.title'),
      key: 'sendMails',
      icon: <MailOutlined />,
      children: [
        {
          key: 'sendMail-waiting_capital_deposit',
          label: t('menuActions.sendMail.waiting_capital_deposit'),
        },
        {
          key: 'sendMail-formality_done',
          label: t('menuActions.sendMail.formality_done'),
        },
        // TODO: re-enable once the mail service migration (SendGrid -> legacy) is done
        // {
        //   key: 'sendMail-form_recap',
        //   label: t('menuActions.sendMail.form_recap'),
        // },
      ],
    },
    {
      label: t('menuActions.downloadFormRecap.title'),
      key: 'downloadFormRecap',
      icon: <DownloadOutlined />,
      disabled: !legalFormality.formRecapGenerated,
    },
    {
      label: t('menuActions.stepBack.title'),
      key: 'stepBack',
      icon: <BackwardFilled />,
      children: listStepBack(),
    },
    {
      label: t('menuActions.resetFormality.title'),
      key: 'resetFormality',
      icon: <FastBackwardOutlined />,
      children: [
        {
          key: 'resetFormality-soft',
          label: t('menuActions.resetFormality.soft'),
        },
        {
          key: 'resetFormality-hard',
          label: t('menuActions.resetFormality.hard'),
        },
      ],
    },
    {
      label: t('menuActions.regenerateStaffs.title'),
      key: 'regenerateStaffs',
      icon: <TeamOutlined />,
    },
  ]

  return (
    <>
      <DropdownOverlayStyles />
      <Dropdown
        overlayClassName="formality-actions-dropdown"
        menu={{ items: buildMenuItems(), onClick }}
        trigger={['click']}
      >
        <Space
          style={{
            backgroundColor: 'white',
            padding: '5px 8px 3px 8px',
            borderRadius: '8px',
            border: '1px solid black',
          }}
        >
          <SettingOutlined
            style={{
              fontSize: '22px',
              color: 'black',
            }}
          />
        </Space>
      </Dropdown>
    </>
  )
}

export default FormalityActionsMenu

const DropdownOverlayStyles = createGlobalStyle`
  .formality-actions-dropdown .ant-dropdown-menu-submenu-title {
    padding-left: 46px;
    padding-right: 12px;
  }

  .formality-actions-dropdown .ant-dropdown-menu-submenu-arrow {
    left: 16px !important;
    right: auto !important;
  }

  .formality-actions-dropdown .ant-dropdown-menu-item-icon,
  .formality-actions-dropdown .ant-dropdown-menu-submenu-title .anticon:not(.ant-dropdown-menu-submenu-arrow-icon) {
    margin-left: 24px !important;
    margin-right: 10px !important;
  }
`
